// src/store/stream/agentInvoke.ts
/**
 * .tuMetrics {"tu1": 0.88, "tu2": 0.9, "tu3": 7, "notes": "The side-effecting half of the agent trigger: builds the orchestrator payload from pure decisions in agentTrigger.ts, drives typing indicators through injected callbacks, and calls the edge functions. Browser-only (window.location, supabase)", "tuVersion": "2.5"}
 * .help "After a person's message is saved, wakes the right agents: shows them typing, sends the orchestrator (or TeamBots) a short transcript plus where the user is, and records which agents are on the session."
 */
import type { ChatSession, Comment, User } from '../../types';
import { supabase } from '../../lib/supabase';
import {
  agentIdsForSession,
  buildConversationContext,
  decideAgentRelay,
  detectAgentView,
  detectUserLocation,
  isHelpSessionMessage,
  selectAgentsToInvoke,
} from './agentTrigger';

export interface AgentInvokeMessage {
  streamType: Comment['streamType'];
  content: string;
  mentions?: string[];
  tags?: string[];
}

export interface AgentInvokeContext {
  message: AgentInvokeMessage;
  recipientId: string;
  currentUser: User;
  allUsers: User[];
  /** Live store reads — resolved at call time so the transcript reflects the optimistic insert. */
  getState: () => {
    chatSessions: ChatSession[];
    messages: Comment[];
    activeStreamType?: Comment['streamType'];
    activeRecipientStreamId?: string;
    updateTypingStatus: (userId: string, at: number) => void;
  };
  /** Does `m` belong to the stream the user is looking at? (owned by streamStore) */
  belongsToActive: (
    m: Comment,
    activeType: Comment['streamType'] | undefined,
    activeRecipientId: string | undefined,
    me: string,
  ) => boolean;
  /** Typing keep-alive, owned by streamStore so realtime replies can stop it. */
  typing: { start: (agentId: string) => void };
}

/**
 * Wake agents for a just-sent message. Never throws: agent failures must not undo a sent
 * message, so everything is caught and logged here.
 */
export async function invokeAgentsForMessage(ctx: AgentInvokeContext): Promise<void> {
  const { message, recipientId, currentUser, allUsers, getState } = ctx;
  try {
    const mentions = message.mentions ?? [];
    const selection = selectAgentsToInvoke({
      streamType: message.streamType,
      recipientId,
      mentions,
      allUsers,
      sessions: getState().chatSessions,
    });
    if (!selection.primary) return;

    const primary = selection.primary;
    ctx.typing.start(primary);

    const me = currentUser.id;
    const state = getState();
    const conversation = buildConversationContext({
      messages: state.messages.filter((m) =>
        ctx.belongsToActive(m, state.activeStreamType, state.activeRecipientStreamId, me),
      ),
      users: allUsers,
      current: { content: message.content, senderId: me },
    });

    const { getGlobalTrainingContext } = await import('../../contexts/TrainingContext');
    const trainingContext = getGlobalTrainingContext();

    const pathname = window.location.pathname;
    const isHelpSession = isHelpSessionMessage(message.tags, message.content);
    const { view, activity } = detectAgentView({
      streamType: message.streamType,
      tags: message.tags,
      isHelpSession,
      pathname,
    });
    const userLocation = detectUserLocation(pathname, message.tags);

    let participants: { userIds: string[]; agentIds: string[]; standupName?: string } | null = null;
    if (message.streamType === 'standup_stream') {
      const session = state.chatSessions.find(
        (s) => s.streamType === 'standup_stream' && s.recipientStreamId === recipientId,
      );
      if (session) {
        participants = {
          userIds: session.userIds || [],
          agentIds: session.agentIds || [],
          standupName: session.name || undefined,
        };
      }
    }

    const currentTurn = conversation[conversation.length - 1];
    const payload = {
      sender_id: me,
      agent_id: primary,
      company_id: currentUser.companyId,
      conversation_context: conversation,
      original_prompt: currentTurn?.content ?? message.content,
      original_stream_type: message.streamType,
      original_recipient_stream_id: recipientId,
      is_help_session: isHelpSession,
      training_context: trainingContext,
      view,
      activity,
      user_location: userLocation,
      participants,
    };

    const invoked = allUsers.find((u) => u.id === primary);
    const viaTeambots = invoked?.externalAgentSource === 'teambots' && invoked.teambotsAgentId;

    if (viaTeambots) {
      const { error } = await supabase.functions.invoke('teambots-integration', {
        body: {
          action: 'currents_chat',
          companyId: currentUser.companyId,
          teambotsAgentId: invoked.teambotsAgentId,
          localAgentId: primary,
          senderId: me,
          streamType: message.streamType,
          recipientStreamId: recipientId,
          messages: conversation,
          activityType: 'channel_message',
          appContext: {
            streamType: message.streamType,
            streamId: recipientId,
            isHelpSession,
            senderId: me,
            senderDisplayName: currentUser.casualName || currentUser.name,
            senderName: currentUser.name,
            // Edge function overwrites with auth.getUser() email when present.
            senderEmail: currentUser.email || undefined,
            userLocation,
          },
        },
      });
      if (error) console.error('[agentInvoke] teambots-integration currents_chat:', error.message);
    } else {
      const { error } = await supabase.functions.invoke('agent-orchestrator', { body: payload });
      if (error) console.error('[agentInvoke] agent-orchestrator:', error.message);
    }

    // Meetings: the other invited agents get the same transcript and decide for themselves.
    for (const agentId of selection.all.slice(1)) {
      getState().updateTypingStatus(agentId, Date.now());
      const peer = allUsers.find((u) => u.id === agentId);
      const peerTeambots = peer?.externalAgentSource === 'teambots' && peer.teambotsAgentId;
      if (peerTeambots) {
        supabase.functions
          .invoke('teambots-integration', {
            body: {
              action: 'currents_chat',
              companyId: currentUser.companyId,
              teambotsAgentId: peer.teambotsAgentId,
              localAgentId: agentId,
              senderId: me,
              streamType: message.streamType,
              recipientStreamId: recipientId,
              messages: conversation,
              activityType: 'channel_message',
              appContext: {
                streamType: message.streamType,
                streamId: recipientId,
                isHelpSession,
                senderId: me,
                senderDisplayName: currentUser.casualName || currentUser.name,
                senderName: currentUser.name,
                senderEmail: currentUser.email || undefined,
                userLocation,
              },
            },
          })
          .then(({ error }) => {
            if (error) console.error(`[agentInvoke] teambots peer (${agentId}):`, error.message);
          })
          .catch((e) => console.error(`[agentInvoke] teambots peer (${agentId}):`, e));
      } else {
        supabase.functions
          .invoke('agent-orchestrator', { body: { ...payload, agent_id: agentId } })
          .then(({ error }) => {
            if (error) console.error(`[agentInvoke] agent-orchestrator (${agentId}):`, error.message);
          })
          .catch((e) => console.error(`[agentInvoke] agent-orchestrator (${agentId}):`, e));
      }
    }

    await supabase.functions.invoke('manage-chat-session', {
      body: {
        stream_type: message.streamType,
        recipient_stream_id: recipientId,
        user_id: me,
        agent_ids: agentIdsForSession({ streamType: message.streamType, mentions, allUsers }, selection),
        company_id: currentUser.companyId,
      },
    });
  } catch (e) {
    console.error('[agentInvoke] failed (message already sent):', e);
  }
}

export interface AgentRelayContext {
  /** The agent's message that just arrived over realtime. */
  arrived: Comment;
  me: string;
  allUsers: User[];
  getState: () => {
    chatSessions: ChatSession[];
    messages: Comment[];
    updateTypingStatus: (userId: string, at: number) => void;
  };
}

/**
 * An agent spoke in a meeting: if this client is the host (see decideAgentRelay), let the other
 * invited agents hear it. Called after the realtime reducer has stored the row — never from
 * inside `set()`. Never throws.
 */
export function relayAgentMessageToPeers(ctx: AgentRelayContext): void {
  const { arrived, me, allUsers, getState } = ctx;
  try {
    const state = getState();
    const decision = decideAgentRelay({
      me,
      arrived,
      messages: state.messages,
      allUsers,
      sessions: state.chatSessions,
    });
    if (!decision.relay) return;

    const session = state.chatSessions.find(
      (s) => s.streamType === 'standup_stream' && s.recipientStreamId === arrived.recipientStreamId,
    );
    const conversation = buildConversationContext({
      messages: state.messages,
      users: allUsers,
      current: { content: arrived.content, senderId: arrived.userId },
    });
    const payload = {
      sender_id: arrived.userId,
      company_id: arrived.companyId,
      conversation_context: conversation,
      original_prompt: arrived.content,
      original_stream_type: arrived.streamType,
      original_recipient_stream_id: arrived.recipientStreamId,
      is_help_session: false,
      view: 'standup_chat',
      activity: 'group_conversation',
      user_location: detectUserLocation(window.location.pathname),
      participants: session
        ? { userIds: session.userIds || [], agentIds: session.agentIds || [], standupName: session.name || undefined }
        : null,
    };

    for (const agentId of decision.peers) {
      getState().updateTypingStatus(agentId, Date.now());
      supabase.functions
        .invoke('agent-orchestrator', { body: { ...payload, agent_id: agentId } })
        .then(({ error }) => {
          if (error) console.error(`[agentInvoke] relay → ${agentId}:`, error.message);
        })
        .catch((e) => console.error(`[agentInvoke] relay → ${agentId}:`, e));
    }
  } catch (e) {
    console.error('[agentInvoke] relay failed:', e);
  }
}
